import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFavourites } from '../context/FavouritesContext';
import { resolveBuilderLogo } from '../utils/builderLogoOverrides';
import ShortlistLandRecommender from './ShortlistLandRecommender';
import { X, Heart, Trash2, Send, Bed, Bath, Ruler, Building2, Loader2, CheckCircle2, Home, Maximize2, MapPin, Layers, BookOpen, ShoppingBag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '';
};

// ─────────────────────────────────────────────────────────────────────
// Tab definitions - render in this order in the tab bar.
// ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'plan',       label: 'Designs',  ctaNoun: 'home designs',    emptyMsg: 'No home designs shortlisted yet.', emptyHint: 'Tap the heart on any home design to save it here.' },
  { id: 'land',       label: 'Land',     ctaNoun: 'land blocks',     emptyMsg: 'No land blocks shortlisted yet.',  emptyHint: 'Tap the heart on a land listing to save it here.' },
  { id: 'hl_package', label: 'Packages', ctaNoun: 'house & land packages', emptyMsg: 'No H&L packages shortlisted yet.', emptyHint: 'Tap the heart on any house & land package to save it.' },
  { id: 'builder',    label: 'Builders', ctaNoun: 'builders',        emptyMsg: 'No builders shortlisted yet.',     emptyHint: 'Use Find a Builder, then tap "Save to Shortlist" on any matched card.' },
  { id: 'product',    label: 'Products', ctaNoun: 'products',        emptyMsg: 'No products saved to your build yet.', emptyHint: 'Tap "Save to my build" on any product to keep it here.' },
  { id: 'display_home', label: 'Display homes', ctaNoun: 'display homes', emptyMsg: 'No display homes saved yet.', emptyHint: 'Tap the heart on any display home to save it here.' },
];

// ─────────────────────────────────────────────────────────────────────
// Per-kind row renderers - kept small and focused so each one can
// surface the bits of metadata that actually matter for that kind.
// ─────────────────────────────────────────────────────────────────────
const PlanRow = ({ plan, onRemove, onClose }) => (
  <Link
    to={`/home-designs/${plan.builder_slug}/${plan.plan_slug}`}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-[#e8e8ed]">
      {plan.facade && !plan.facade.includes('undefined') ? (
        <img src={plan.facade} alt={plan.plan_name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Building2 className="w-5 h-5 text-[#d2d2d7]" /></div>
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block truncate">
        {plan.plan_name}
      </span>
      <p className="text-xs text-[#86868b]">{plan.builder_name}</p>
      <div className="flex items-center gap-3 text-[10px] text-[#424245] mt-1">
        {plan.bedrooms != null  && <span className="flex items-center gap-0.5"><Bed className="w-3 h-3" />{plan.bedrooms}</span>}
        {plan.bathrooms != null && <span className="flex items-center gap-0.5"><Bath className="w-3 h-3" />{plan.bathrooms}</span>}
        {plan.total_sqm != null && <span className="flex items-center gap-0.5"><Ruler className="w-3 h-3" />{plan.total_sqm?.toFixed(0)}m&sup2;</span>}
      </div>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-fav-${plan.plan_slug}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
);

const LandRow = ({ item, onRemove, onClose }) => {
  const hasFullAddress = item.street_address || item.lot_number;
  const fullAddress = hasFullAddress
    ? [
        item.lot_number ? `Lot ${item.lot_number}` : null,
        item.street_address || null,
        item.suburb ? `${item.suburb}${item.state ? `, ${item.state}` : ''}${item.postcode ? ` ${item.postcode}` : ''}` : null,
      ].filter(Boolean).join(' ')
    : null;
  return (
  <Link
    to={item.detail_path}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-[#e8e8ed]">
      {item.hero_image ? (
        <img src={item.hero_image} alt={`Land in ${item.suburb}`} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><MapPin className="w-5 h-5 text-[#d2d2d7]" /></div>
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block truncate">
        {fullAddress || (item.area_m2 ? `${Math.round(item.area_m2)}m² in ${item.suburb}` : `Land in ${item.suburb}`)}
      </span>
      {fullAddress
        ? <p className="text-xs text-[#86868b] truncate">{item.area_m2 ? `${Math.round(item.area_m2)}m²` : ''}{item.estate_name ? ` · ${item.estate_name}` : ''}</p>
        : (item.estate_name && <p className="text-xs text-[#86868b] truncate">{item.estate_name}</p>)
      }
      <div className="flex items-center gap-3 text-[10px] text-[#424245] mt-1">
        {item.frontage_m && <span className="flex items-center gap-0.5"><Ruler className="w-3 h-3" />{Math.round(item.frontage_m)}m front</span>}
        {item.depth_m && <span className="flex items-center gap-0.5"><Maximize2 className="w-3 h-3" />{Math.round(item.depth_m)}m deep</span>}
      </div>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-land-${item.id}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
  );
};

const HLRow = ({ item, onRemove, onClose }) => {
  // Full address line - appears when the listing came from an auth-gated
  // endpoint. Falls back to "<beds> bed in <suburb>" so anonymous users
  // still see something meaningful.
  const hasFullAddress = item.street_address || item.lot_number;
  const fullAddress = hasFullAddress
    ? [
        item.lot_number ? `Lot ${item.lot_number}` : null,
        item.street_address || null,
        item.suburb ? `${item.suburb}${item.state ? `, ${item.state}` : ''}${item.postcode ? ` ${item.postcode}` : ''}` : null,
      ].filter(Boolean).join(' ')
    : null;
  return (
  <Link
    to={item.detail_path}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-[#e8e8ed]">
      {item.hero_image ? (
        <img src={item.hero_image} alt={`Package in ${item.suburb}`} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Home className="w-5 h-5 text-[#d2d2d7]" /></div>
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block truncate">
        {fullAddress || `${item.bedrooms ? `${item.bedrooms} bed` : 'Home'}${item.bathrooms ? ` · ${item.bathrooms} bath` : ''} in ${item.suburb}`}
      </span>
      {/* When we have the full address up top, surface beds/baths +
          builder on the secondary line for context. */}
      {fullAddress ? (
        <p className="text-xs text-[#86868b] truncate">
          {item.bedrooms ? `${item.bedrooms} bed` : 'Home'}{item.bathrooms ? ` · ${item.bathrooms} bath` : ''}{item.builder_name ? ` · ${item.builder_name}` : ''}
        </p>
      ) : (
        item.builder_name && <p className="text-xs text-[#86868b] truncate">{item.builder_name}</p>
      )}
      <div className="flex items-center gap-3 text-[10px] text-[#424245] mt-1">
        <span className="flex items-center gap-0.5"><Layers className="w-3 h-3" />{item.is_double_storey ? 'Double' : 'Single'}</span>
        {item.total_sqm != null && <span className="flex items-center gap-0.5"><Ruler className="w-3 h-3" />{item.total_sqm?.toFixed(0)}m&sup2;</span>}
      </div>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-hl-${item.id}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
  );
};

const BuilderRow = ({ item, onRemove, onClose }) => {
  const logoSrc = resolveBuilderLogo({ slug: item.builder_slug, logo_url: item.logo_url });
  const builderPath = `/builders/${item.builder_slug || (item.business_name || '').toLowerCase().replace(/\s+/g, '-')}`;
  return (
  <Link
    to={builderPath}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-[#e8e8ed]">
      {logoSrc ? (
        <img src={logoSrc} alt={item.business_name} className="max-w-full max-h-full object-contain p-1" loading="lazy" />
      ) : (
        <Building2 className="w-5 h-5 text-[#d2d2d7]" />
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block truncate">
        {item.business_name}
      </span>
      <p className="text-xs text-[#86868b] truncate">
        {item.regions_display || item.state || ''}
        {item.builder_size ? ` · ${item.builder_size}` : ''}
      </p>
      {item.match_score != null && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] font-bold text-[#1d8efe]">{Math.round(item.match_score)}% match</span>
        </div>
      )}
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-builder-${item.builder_slug || item.id}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Suburb + Article rows - plain bookmarks, no contact form involvement.
// ─────────────────────────────────────────────────────────────────────
const SuburbRow = ({ item, onRemove, onClose }) => (
  <Link
    to={item.detail_path}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-[#e8e8ed] flex items-center justify-center">
      {item.hero_image ? (
        <img src={item.hero_image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <MapPin className="w-5 h-5 text-[#d2d2d7]" />
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block truncate">{item.name}</span>
      <p className="text-xs text-[#86868b]">
        {item.state || 'SA'}{item.postcode ? ` · ${item.postcode}` : ''}
      </p>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-suburb-${item.suburb_slug}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
);

const ArticleRow = ({ item, onRemove, onClose }) => (
  <Link
    to={item.detail_path}
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-[#e8e8ed] flex items-center justify-center">
      {item.hero_image ? (
        <img src={item.hero_image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <BookOpen className="w-5 h-5 text-[#d2d2d7]" />
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block line-clamp-2 leading-snug">{item.title}</span>
      {item.summary && <p className="text-xs text-[#86868b] mt-0.5 line-clamp-1">{item.summary}</p>}
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-article-${item.slug}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
);

// Product rows link straight out to the tracked partner (go_url) so the
// user can pick up buying where they left off.
const ProductRow = ({ item, onRemove }) => (
  <a
    href={item.go_url ? `${API}${item.go_url}` : '#'}
    target="_blank"
    rel="sponsored nofollow noopener noreferrer"
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-[#e8e8ed]">
      {item.image ? (
        <img src={item.image} alt={item.name} className="max-w-full max-h-full object-contain p-1" loading="lazy" />
      ) : (
        <ShoppingBag className="w-5 h-5 text-[#d2d2d7]" />
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block line-clamp-2 leading-snug">{item.name}</span>
      <div className="flex items-center gap-2 mt-1">
        {money(item.price) && <span className="text-xs font-bold text-[#1d1d1f]">{money(item.price)}</span>}
        {item.save > 0 && money(item.list_price) && <span className="text-[10px] text-[#9aa0a8] line-through">{money(item.list_price)}</span>}
        {item.supplier?.name && <span className="text-[10px] text-[#86868b] truncate">from {item.supplier.name}</span>}
      </div>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-product-${item.id}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </a>
);

const DisplayHomeRow = ({ item, onClose, onRemove }) => (
  <Link
    to="/display-homes"
    onClick={onClose}
    className="flex gap-3 bg-[#f5f5f7] rounded-xl p-3 relative group hover:bg-[#eeeef0] transition-colors no-underline"
  >
    <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-[#e8e8ed]">
      {item.hero_image ? (
        <img src={item.hero_image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Home className="w-5 h-5 text-[#d2d2d7]" />
      )}
    </div>
    <div className="flex-1 min-w-0 pr-6">
      <span className="font-medium text-sm text-[#1d1d1f] block line-clamp-2 leading-snug">{item.name}</span>
      <div className="text-[11px] text-[#86868b] mt-1 truncate">
        {[item.builder_name, item.suburb, item.state].filter(Boolean).join(' · ')}
      </div>
    </div>
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-[var(--bp-color-surface-tinted)] text-[#86868b] hover:text-red-500 transition-colors z-10"
      title="Remove"
      data-testid={`remove-display-home-${item.id}`}
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </Link>
);const FavouritesSidebar = () => {
  const {
    plansList, landList, hlList, buildersList, articlesList, suburbsList, productsList, displayHomesList,
    count, countByKind,
    sidebarOpen, setSidebarOpen,
    sidebarTab, setSidebarTab,
    toggleFavourite, clearKind,
  } = useFavourites();

  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  // 2026-02-15: explicit consent now required (was missing entirely).
  const [consent, setConsent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    if (!consent) {
      setError('Please accept the Privacy Policy + Terms to continue.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/home-designs/enquire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          plans: plansList.map(p => ({
            plan_name: p.plan_name || '',
            builder_name: p.builder_name || '',
            bedrooms: p.bedrooms || 0,
            bathrooms: p.bathrooms || 0,
            total_sqm: p.total_sqm || 0,
            stories: p.stories || '',
            url: `https://buildpilot.com.au/home-designs/${p.builder_slug}/${p.plan_slug}`,
          })),
          privacy_consent: true,
          source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        }),
      });
      if (res.ok) {
        setSent(true);
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        let errorMsg = 'Something went wrong. Please try again.';
        if (data.detail) {
          if (typeof data.detail === 'string') errorMsg = data.detail;
          else if (Array.isArray(data.detail)) errorMsg = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        setError(errorMsg);
      }
    } catch {
      setError('Could not send. Please try again.');
    }
    setSending(false);
  };

  if (!sidebarOpen) return null;

  const planCount = countByKind.plan || 0;
  const landCount = countByKind.land || 0;
  const hlCount   = countByKind.hl_package || 0;
  const builderCount = countByKind.builder || 0;
  const productCount = countByKind.product || 0;
  const displayHomeCount = countByKind.display_home || 0;
  const suburbCount  = countByKind.suburb || 0;
  const articleCount = countByKind.article || 0;
  const tabCount  = (id) => {
    if (id === 'plan') return planCount;
    if (id === 'land') return landCount;
    if (id === 'hl_package') return hlCount;
    if (id === 'builder') return builderCount;
    if (id === 'product') return productCount;
    if (id === 'display_home') return displayHomeCount;
    if (id === 'suburb')  return suburbCount;
    if (id === 'article') return articleCount;
    return 0;
  };

  const activeList = (() => {
    if (sidebarTab === 'land') return landList;
    if (sidebarTab === 'hl_package') return hlList;
    if (sidebarTab === 'builder') return buildersList;
    if (sidebarTab === 'product') return productsList;
    if (sidebarTab === 'display_home') return displayHomesList;
    if (sidebarTab === 'suburb')  return suburbsList;
    if (sidebarTab === 'article') return articlesList;
    return plansList;
  })();
  const activeTabMeta = TABS.find(t => t.id === sidebarTab) || TABS[0];

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm"
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-overlay"
      />

      {/* Sidebar */}
      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white/85 bp-glass-surface bp-glass-surface--strong bp-glass-edge z-[70] shadow-2xl flex flex-col animate-slide-in"
        data-testid="favourites-sidebar"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8e8ed]">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
            <h2 className="text-lg font-semibold text-[#1d1d1f]">
              My Shortlist {count > 0 && <span className="text-[#86868b] font-normal">({count})</span>}
            </h2>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f5f7] transition-colors"
            data-testid="close-sidebar"
          >
            <X className="w-5 h-5 text-[#86868b]" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1.5 px-3 pt-3 pb-2 border-b border-[#f0f0f3]" role="tablist" aria-label="Shortlist categories">
          {TABS.map((t) => {
            const isActive = sidebarTab === t.id;
            const n = tabCount(t.id);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setSidebarTab(t.id)}
                data-testid={`shortlist-tab-${t.id}`}
                className={`flex-1 px-2 py-2 text-xs font-semibold rounded-lg transition-colors ${
                  isActive
                    ? 'bg-[#1d1d1f] text-white'
                    : 'bg-[#f5f5f7] text-[#1d1d1f] hover:bg-[#e8e8ed]'
                }`}
              >
                {t.label}
                {n > 0 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                    isActive ? 'bg-white/20 text-white' : 'bg-[#1d1d1f] text-white'
                  }`}>{n}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeList.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <Heart className="w-12 h-12 text-[#d2d2d7] mb-3" />
              <p className="text-[#1d1d1f] font-medium mb-1">{activeTabMeta.emptyMsg}</p>
              <p className="text-sm text-[#86868b]">{activeTabMeta.emptyHint}</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {sidebarTab === 'plan'       && plansList.map(p => (
                <PlanRow key={p.full_slug} plan={p} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(p, 'plan')} />
              ))}
              {sidebarTab === 'land'       && landList.map(item => (
                <LandRow key={item.id || item.listing_slug} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'land')} />
              ))}
              {sidebarTab === 'hl_package' && hlList.map(item => (
                <HLRow key={item.id || item.listing_slug} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'hl_package')} />
              ))}
              {sidebarTab === 'builder'    && buildersList.map(item => (
                <BuilderRow key={item.builder_slug || item.id || item.business_name} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'builder')} />
              ))}
              {sidebarTab === 'product'    && productsList.map(item => (
                <ProductRow key={item.id} item={item} onRemove={() => toggleFavourite(item, 'product')} />
              ))}
              {sidebarTab === 'display_home' && displayHomesList.map(item => (
                <DisplayHomeRow key={item.id} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'display_home')} />
              ))}
              {sidebarTab === 'suburb'     && suburbsList.map(item => (
                <SuburbRow key={item.suburb_slug} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'suburb')} />
              ))}
              {sidebarTab === 'article'    && articlesList.map(item => (
                <ArticleRow key={item.slug} item={item} onClose={() => setSidebarOpen(false)} onRemove={() => toggleFavourite(item, 'article')} />
              ))}

              {activeList.length > 1 && (
                <button
                  onClick={() => clearKind(sidebarTab)}
                  className="flex items-center gap-1.5 text-xs text-[#86868b] hover:text-red-500 transition-colors mx-auto mt-1"
                  data-testid={`clear-shortlist-${sidebarTab}`}
                >
                  <Trash2 className="w-3 h-3" /> Clear {activeTabMeta.label}
                </button>
              )}

              {/* Shortlist-driven land recommender - only on the Designs tab */}
              {sidebarTab === 'plan' && plansList.length > 0 && (
                <ShortlistLandRecommender
                  planSlugs={plansList.map(p => `${p.builder_slug}/${p.plan_slug}`)}
                  onLinkClick={() => setSidebarOpen(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer - enquiry form is plan-specific. Land + Packages get a
            simpler "Send my shortlist" message that funnels into the
            same endpoint as a follow-up enhancement (TODO). */}
        {sidebarTab === 'plan' && planCount > 0 && (
          <div className="border-t border-[#e8e8ed] bg-white">
            {sent ? (
              <div className="p-5 text-center">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-[#1d1d1f] mb-1">Enquiry Sent!</p>
                <p className="text-sm text-[#86868b]">We&apos;ve received your shortlist and will be in touch soon.</p>
                <button onClick={() => { setSent(false); setShowForm(false); }} className="text-sm text-[#1d8efe] hover:underline mt-3">Done</button>
              </div>
            ) : !showForm ? (
              <div className="p-4">
                <button
                  onClick={() => setShowForm(true)}
                  className="w-full flex items-center justify-center gap-2 bg-[#1d1d1f] text-white py-3 rounded-xl font-medium text-sm hover:bg-[#2d2d2f] transition-colors"
                  data-testid="enquire-now-btn"
                >
                  <Send className="w-4 h-4" />
                  Enquire About {planCount} Plan{planCount > 1 ? 's' : ''}
                </button>
                <p className="text-[10px] text-[#86868b] text-center mt-2">Independent advice from our team</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 space-y-3" data-testid="enquiry-form">
                <p className="text-sm font-medium text-[#1d1d1f]">Send us your shortlist</p>
                <input type="text"  placeholder="Your name *"   required value={form.name}    onChange={e => setForm(p => ({ ...p, name:    e.target.value }))} className="w-full px-3 py-2.5 bg-[#f5f5f7] rounded-lg text-sm border-0 focus:ring-2 focus:ring-[#1d8efe] outline-none" data-testid="enquiry-name" />
                <input type="email" placeholder="Email address *" required value={form.email}  onChange={e => setForm(p => ({ ...p, email:   e.target.value }))} className="w-full px-3 py-2.5 bg-[#f5f5f7] rounded-lg text-sm border-0 focus:ring-2 focus:ring-[#1d8efe] outline-none" data-testid="enquiry-email" />
                <input type="tel"   placeholder="Phone number"                   value={form.phone}  onChange={e => setForm(p => ({ ...p, phone:   e.target.value }))} className="w-full px-3 py-2.5 bg-[#f5f5f7] rounded-lg text-sm border-0 focus:ring-2 focus:ring-[#1d8efe] outline-none" data-testid="enquiry-phone" />
                <textarea placeholder="Any questions or notes? (optional)" rows={2} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} className="w-full px-3 py-2.5 bg-[#f5f5f7] rounded-lg text-sm border-0 focus:ring-2 focus:ring-[#1d8efe] outline-none resize-none" data-testid="enquiry-message" />
                <label className="flex items-start gap-2 text-[11.5px] leading-[1.5] text-[#535357] cursor-pointer" data-testid="enquiry-consent-wrap">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-[2px] w-4 h-4 shrink-0 rounded border border-[#86868b] accent-[#1d8efe] cursor-pointer"
                    data-testid="enquiry-consent"
                  />
                  <span>
                    I agree to BuildPilot&apos;s{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1d8efe] font-medium hover:underline">Terms</a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1d8efe] font-medium hover:underline">Privacy Policy</a>
                    , and acknowledge BuildPilot may share my shortlist with suitable builders.
                  </span>
                </label>
                {error && <p className="text-xs text-red-500">{error}</p>}
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-[#f5f5f7] rounded-lg text-sm text-[#1d1d1f] hover:bg-[#e8e8ed] transition-colors">Back</button>
                  <button type="submit" disabled={sending} className="flex-1 flex items-center justify-center gap-2 bg-[#1d1d1f] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#2d2d2f] transition-colors disabled:opacity-50" data-testid="submit-enquiry">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {sending ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Generic CTA for land + H&L - links into Find-a-Builder so the
            team can broker the intro. Enquiry form upgrade is TODO. */}
        {sidebarTab !== 'plan' && activeList.length > 0 && (
          <div className="border-t border-[#e8e8ed] bg-white p-4">
            <Link
              to="/contact"
              onClick={() => setSidebarOpen(false)}
              className="w-full flex items-center justify-center gap-2 bg-[#1d1d1f] text-white py-3 rounded-xl font-medium text-sm hover:bg-[#2d2d2f] transition-colors"
              data-testid={`enquire-${sidebarTab}-btn`}
            >
              <Send className="w-4 h-4" />
              Get one-on-one support for these {activeTabMeta.ctaNoun}
            </Link>
            <p className="text-[10px] text-[#86868b] text-center mt-2">Independent, builder-agnostic advice</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideIn 0.25s ease-out; }
      `}</style>
    </>
  );
};

export default FavouritesSidebar;
